import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { currencyFormat } from "../../../helpers/utils";
import { isCompanyMemberOrCounselForDeal } from "../../../helpers/utilsTS";
import DKFormContainer from "../../common/forms/formContainer/DKFormContainer";
import DealInformationForm from "../createDeals/dealInformationForm";
import * as api from "../../../api/generated-api";
import { getDealById, updateDeal } from "../../../api/deals/deals";
import {
  configureAuthHeader,
  configureBaseUrl,
} from "../../../api/axiosConfig";
import { Box, Typography } from "@mui/material";
import { getDealLegalEntitiesForUser } from "../../../api/deals/deallegalentities";
import { DealsPageLayout } from "../dealsPageLayout/DealsPageLayout";
import { DealPageActionButtonInfo } from "../dealsPageLayout/DealsPageHeader/DealsPageHeader";
import { RouteComponentProps } from "react-router-dom";
import { getAllDealLegalEntityInvestmentsForDeal } from "../../../api/deals/investors";

type DealInformationProps = RouteComponentProps<{
  orgId?: string;
  dealId?: string;
}> & {
  user: {
    person: {
      organizations_persons: {
        organization_id: number;
        role_id: number;
        person_id: number;
      }[];
    };
  };
  token: string;
};

export const emptyDealSeriesProperties: Omit<
  api.DealSeries,
  "name" | "pricePerShare" | "isPrimary"
> = {
  id: undefined,
  createdAt: undefined,
  updatedAt: undefined,
  organizationId: undefined,
  dealId: undefined,
};

const DealInformationPage = (props: DealInformationProps) => {
  const { user, token } = props;
  const orgId = Number(props.match.params.orgId);
  const dealId = Number(props.match.params.dealId);

  configureBaseUrl();
  configureAuthHeader(token);

  const [deal, setDeal] = useState<api.Deal>(null);
  const [formDeal, setFormDeal] = useState<api.DealCreate>(null);
  const [formDealSeries, setFormDealSeries] = useState<api.DealSeries[]>([
    {
      ...emptyDealSeriesProperties,
      name: "Series Seed",
      pricePerShare: undefined,
      isPrimary: true,
    },
  ]);
  const [editMode, setEditMode] = useState(false);
  const [dealLegalEntitiesForUser, setDealLegalEntitiesForUser] = useState<
    api.DealLegalEntity[]
  >([]);
  const [investments, setInvestments] = useState<
    api.DealLegalEntityInvestment[]
  >([]);

  useEffect(() => {
    getDealLegalEntitiesForUser(orgId, dealId, (fetchedDealLegalEntities) => {
      setDealLegalEntitiesForUser(fetchedDealLegalEntities);
    });

    getAllDealLegalEntityInvestmentsForDeal(
      orgId,
      dealId,
      (fetchedInvestments) => {
        setInvestments(fetchedInvestments);
      }
    );
  }, [orgId, dealId]);

  useEffect(() => {
    getDealById(orgId, dealId, (fetchedDeal) => {
      setDeal(fetchedDeal);
    });
  }, [orgId, dealId]);

  useEffect(() => {
    if (deal) {
      setFormDeal(deal);
      setFormDealSeries(deal.series);
    }
  }, [deal]);

  const saveUpdatedDeal = (deal: api.DealUpdate) => {
    deal.series = formDealSeries;
    updateDeal(orgId, dealId, deal, (savedDeal) => {
      setDeal(savedDeal);
    });
  };

  type DealInformationRowProps = {
    id?: string;
    label: string;
    value: string;
    dataTestId: string;
  };

  const DealInformationRow = ({
    id,
    label,
    value,
    dataTestId,
  }: DealInformationRowProps) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        marginBottom: "16px",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: "bold",
        }}
      >
        {label}
      </Typography>
      <Typography id={id} variant="body1" data-testid={dataTestId}>
        {value}
      </Typography>
    </Box>
  );

  if (!dealLegalEntitiesForUser) {
    return null;
  }

  const isCompanyMemberOrCounsel = isCompanyMemberOrCounselForDeal(
    user.person.organizations_persons,
    orgId,
    dealId,
    dealLegalEntitiesForUser
  );

  const actionButtons: DealPageActionButtonInfo[] = [];

  if (!editMode && isCompanyMemberOrCounsel) {
    actionButtons.push({
      dataTestId: "deal-information-edit-button",
      label: "edit",
      primary: true,
      onClick: () => {
        setEditMode(true);
      },
    });
  }

  if (editMode && isCompanyMemberOrCounsel) {
    actionButtons.push({
      dataTestId: "deal-information-save-button",
      label: "save",
      primary: true,
      onClick: () => {
        saveUpdatedDeal({
          seriesOfPreferredStockSold: formDeal.seriesOfPreferredStockSold,
          valuation: formDeal.valuation,
          totalRoundSize: formDeal.totalRoundSize,
        });
        setEditMode(false);
      },
    });
  }

  return (
    <DealsPageLayout title={"Deal Information"} actionButtons={actionButtons}>
      {formDeal && (
        <>
          {!editMode && (
            <DKFormContainer>
              <DealInformationRow
                label="Round"
                value={formDeal.seriesOfPreferredStockSold}
                id="deal-information-round"
                dataTestId={"deal-information-round"}
              />
              <DealInformationRow
                label="Total Amount of New Cash Investment"
                value={currencyFormat(formDeal.totalRoundSize)}
                dataTestId={"deal-information-total-round-size"}
              />
              <DealInformationRow
                label="Pre-Money Valuation"
                value={currencyFormat(formDeal.valuation)}
                dataTestId={"deal-information-valuation"}
              />
              {formDealSeries.length === 1 && (
                <DealInformationRow
                  label="Price Per Share"
                  value={currencyFormat(formDealSeries[0].pricePerShare)}
                  dataTestId={"deal-information-price-per-share"}
                />
              )}
              {formDealSeries.length > 1 &&
                formDealSeries.map((series) => {
                  return (
                    <DealInformationRow
                      key={series.name}
                      label={series.name}
                      value={currencyFormat(series.pricePerShare)}
                      dataTestId={"deal-information-price-per-share"}
                    />
                  );
                })}
            </DKFormContainer>
          )}
          {isCompanyMemberOrCounsel && editMode && (
            <DKFormContainer>
              <DealInformationForm
                deal={formDeal}
                dealSeries={formDealSeries}
                dealLegalEntityInvestments={investments}
                onDealChange={(updatedDeal) =>
                  setFormDeal({
                    totalRoundSize: updatedDeal.totalRoundSize,
                    valuation: updatedDeal.valuation,
                    seriesOfPreferredStockSold:
                      updatedDeal.seriesOfPreferredStockSold,
                    series: undefined,
                  })
                }
                onDealSeriesChange={(updatedDealSeries) =>
                  setFormDealSeries(updatedDealSeries)
                }
              />
            </DKFormContainer>
          )}
        </>
      )}
    </DealsPageLayout>
  );
};

const mapStateToProps = (state) => {
  const { user, token } = state.auth;
  return {
    user,
    token,
  };
};

export default connect(mapStateToProps)(DealInformationPage);
